import { getFirestore, collection, addDoc, getDocs, getDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { auth, db } from '../firebase/firebaseConfig';

// Initialize Firestore
const firestore = getFirestore();

// Gallery image interface
export interface GalleryImage {
    id?: string;
    imageUrl: string;
    // We keep `publicId` field for future use, but we no longer delete from Cloudinary on the frontend
    publicId?: string;
    title?: string;
    description?: string;
    createdAt: any;
    createdBy: string;
    updatedAt: any;
}

export const GALLERY_DOC_NAME = 'galleryImages';

// Create gallery images (multiple)
export const createGalleryImages = async (imageUrls: string[], title?: string, description?: string): Promise<{ success: boolean, ids?: string[], error?: string }> => {
    try {
        const currentUser = auth.currentUser;

        if (!currentUser) {
            return { success: false, error: 'User not authenticated' };
        }

        if (!imageUrls || imageUrls.length === 0) {
            return { success: false, error: 'No images provided' };
        }

        const ids: string[] = [];

        // Create a document for each image
        for (const imageUrl of imageUrls) {
            const galleryImage: Omit<GalleryImage, 'id'> = {
                imageUrl,
                title: title || '',
                description: description || '',
                createdAt: serverTimestamp(),
                createdBy: currentUser.uid,
                updatedAt: serverTimestamp()
            };

            const docRef = await addDoc(collection(firestore, GALLERY_DOC_NAME), galleryImage);
            ids.push(docRef.id);
        }

        return { success: true, ids };
    } catch (error) {
        console.error('Error creating gallery images:', error);
        return { success: false, error: (error as Error).message };
    }
};

// Get all gallery images
export const getAllGalleryImages = async (): Promise<GalleryImage[]> => {
    try {
        const q = query(collection(firestore, GALLERY_DOC_NAME), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        } as GalleryImage));
    } catch (error) {
        console.error('Error getting gallery images:', error);
        return [];
    }
};

// Get a single gallery image by ID
export const getGalleryImageById = async (id: string): Promise<GalleryImage | null> => {
    try {
        const docRef = doc(firestore, GALLERY_DOC_NAME, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() } as GalleryImage;
        }

        return null;
    } catch (error) {
        console.error('Error getting gallery image:', error);
        return null;
    }
};

// Update a gallery image
export const updateGalleryImage = async (id: string, galleryData: Partial<GalleryImage>): Promise<boolean> => {
    try {
        const docRef = doc(firestore, GALLERY_DOC_NAME, id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            return false;
        }

        const updates: Partial<GalleryImage> = {
            ...galleryData,
            updatedAt: serverTimestamp()
        };

        await updateDoc(docRef, updates);
        return true;
    } catch (error) {
        console.error('Error updating gallery image:', error);
        return false;
    }
};

// Delete a gallery image (Firestore only – no Cloudinary deletion from frontend)
export const deleteGalleryImage = async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
        const docRef = doc(firestore, GALLERY_DOC_NAME, id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            return { success: false, error: 'Image not found' };
        }

        // Only delete the document from Firestore
        await deleteDoc(docRef);
        return { success: true };
    } catch (error) {
        console.error('Error deleting gallery image:', error);
        return { success: false, error: (error as Error).message };
    }
};

